import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, type ReactNode } from 'react';
import type { UiTab } from '../types/uiSchema';

function SortableTabRow({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <Paper ref={setNodeRef} style={style} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: 'text.secondary' }}>
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Paper>
  );
}

type Props = {
  tabs: UiTab[];
  onChangeTabs: (next: UiTab[]) => void;
  onSyncFromFormSchema: () => void;
  disabled: boolean;
};

export default function CanvasHierarchyPanel({ tabs, onChangeTabs, onSyncFromFormSchema, disabled }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => tabs.map((t) => t.id), [tabs]);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(tabs, oldIndex, newIndex).map((t, i) => ({ ...t, order: i }));
    onChangeTabs(next);
  };

  const updateTitle = (tabId: string, title: string) => {
    onChangeTabs(tabs.map((t) => (t.id === tabId ? { ...t, title } : t)));
  };

  const addSubTab = (parentId: string) => {
    const sid = `sub_${Date.now().toString(36)}`;
    onChangeTabs(
      tabs.map((t) => {
        if (t.id !== parentId) return t;
        const sub = [...(t.sub_tabs || [])];
        sub.push({
          id: sid,
          title: `Sub-tab ${sub.length + 1}`,
          order: sub.length,
          sections: [],
        });
        return { ...t, sub_tabs: sub };
      }),
    );
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Form tabs (<code>ui_schema_json.tabs</code>)
      </Typography>
      <Button
        variant="outlined"
        size="small"
        fullWidth
        disabled={disabled}
        onClick={onSyncFromFormSchema}
        sx={{ mb: 1.5 }}
      >
        Sync tabs from form schema
      </Button>
      <Divider sx={{ mb: 1 }} />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <Stack spacing={1}>
            {tabs.map((t) => (
              <SortableTabRow key={t.id} id={t.id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    label="Tab title"
                    value={t.title ?? ''}
                    onChange={(e) => updateTitle(t.id, e.target.value)}
                    disabled={disabled}
                  />
                  <Tooltip title="Add sub-tab (nested)">
                    <span>
                      <IconButton size="small" onClick={() => addSubTab(t.id)} disabled={disabled}>
                        +
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </SortableTabRow>
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
      {tabs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No tabs yet. Use Sync or Generate layout (AI) on Configs.
        </Typography>
      )}
    </Box>
  );
}
